/* ============================================================================
   popup-system.js - controller for the theme-native popup system.
   Loaded deferred by sections/popup-system.liquid.

   Responsibilities: unit targeting (path exclusions, UTM gating, A/B), the
   scroll+dwell engagement gate, teaser, modal + step engine, Klaviyo client
   subscribe (email + SMS) with validation and real error handling,
   suppression, desktop exit intent for the cart unit, discount auto-apply
   redirects, and dataLayer events.

   Preview: ?pl_popup=<unit> forces a unit open, bypassing gates and
   suppression; its events carry preview:true so analytics can exclude them.

   Note on suppression durability: suppression is stored in localStorage and
   mirrored to a cookie. Safari/iOS ITP clears script-writable storage after
   7 days without a first-party interaction, so suppression windows longer
   than 7 days will not always hold on those browsers. Expect some
   over-showing on iOS; this is a browser constraint, not a bug here.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-plps-root]');
  if (!root) return;

  var cfgEl = root.querySelector('[data-plps-config]');
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var KLAVIYO_REVISION = '2024-10-15';

  var LS = {
    get: function (k) { try { return localStorage.getItem('plps:' + k); } catch (e) { return null; } },
    set: function (k, v) { try { localStorage.setItem('plps:' + k, v); } catch (e) {} }
  };

  /* Cookie mirror - covers browsers where localStorage is unavailable
     (private modes, blocked storage). Not a fix for ITP expiry. */
  var CK = {
    get: function (k) {
      var m = document.cookie.match('(?:^|; )plps_' + k.replace(/[^a-z0-9_]/gi, '') + '=([^;]*)');
      return m ? decodeURIComponent(m[1]) : null;
    },
    set: function (k, v, days) {
      try {
        document.cookie = 'plps_' + k.replace(/[^a-z0-9_]/gi, '') + '=' + encodeURIComponent(v) +
          ';path=/;max-age=' + Math.round(days * 86400) + ';SameSite=Lax';
      } catch (e) {}
    }
  };

  /* ---------- visitor state ---------- */
  var visitorId = LS.get('vid') || CK.get('vid');
  if (!visitorId) {
    visitorId = 'v' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
  LS.set('vid', visitorId);
  CK.set('vid', visitorId, 365);

  // Session counting: one increment per browser session.
  var sessions = parseInt(LS.get('sessions') || '0', 10);
  var sessionMarked = false;
  try { sessionMarked = sessionStorage.getItem('plps:session') === '1'; } catch (e) {}
  if (!sessionMarked) {
    sessions += 1;
    LS.set('sessions', String(sessions));
    try { sessionStorage.setItem('plps:session', '1'); } catch (e) {}
  }

  var isStylist = LS.get('stylist') === '1';
  var isSubscribed = LS.get('subscribed') === '1' || CK.get('subscribed') === '1';
  var params = new URLSearchParams(location.search);
  var previewUnit = params.get('pl_popup');
  var isPreview = !!previewUnit;
  var inDesignMode = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- first-touch UTM capture (for per-unit UTM gating) ---------- */
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var sessionUtm = {};
  try {
    var storedUtm = sessionStorage.getItem('plps:utm');
    if (storedUtm) sessionUtm = JSON.parse(storedUtm) || {};
  } catch (e) { sessionUtm = {}; }
  var sawUtmNow = false;
  UTM_KEYS.forEach(function (k) {
    var v = params.get(k);
    if (v) { sessionUtm[k] = v.toLowerCase(); sawUtmNow = true; }
  });
  if (sawUtmNow) {
    try { sessionStorage.setItem('plps:utm', JSON.stringify(sessionUtm)); } catch (e) {}
  }

  /* ---------- suppression ---------- */
  /* The promo unit scopes its dismissal memory to its promo key, so a new
     promo (new key) re-arms for everyone, including people who dismissed the
     last one. Every other unit keys on its own name, unchanged. */
  function supKey(unit) {
    if (unit === 'promo' && cfg.units.promo && cfg.units.promo.promoKey) {
      return 'promo.' + String(cfg.units.promo.promoKey).replace(/[^a-zA-Z0-9_-]/g, '');
    }
    return unit;
  }
  function suppressedUntil(unit) {
    var k = supKey(unit);
    var a = parseInt(LS.get('sup:' + k) || '0', 10);
    var b = parseInt(CK.get('sup_' + k) || '0', 10);
    return Math.max(isNaN(a) ? 0 : a, isNaN(b) ? 0 : b);
  }
  function suppressed(unit) {
    return Date.now() < suppressedUntil(unit);
  }
  function suppress(unit) {
    if (!unit) return;
    var days = (cfg.units[unit] && cfg.units[unit].suppressDays) || 7;
    var until = Date.now() + days * 86400000;
    var k = supKey(unit);
    LS.set('sup:' + k, String(until));
    CK.set('sup_' + k, String(until), days);
  }

  /* Past its configured end date (visitor local time, end of day), the promo
     unit stands down automatically even if the block is still enabled - the
     forgotten-promo safety net. Blank or malformed dates never expire. */
  function promoExpired() {
    var u = cfg.units.promo;
    if (!u || !u.endDate) return false;
    var t = Date.parse(String(u.endDate) + 'T23:59:59');
    return !isNaN(t) && Date.now() > t;
  }

  function markSubscribed() {
    isSubscribed = true;
    LS.set('subscribed', '1');    CK.set('subscribed', '1', 365);
  }

  /* ---------- analytics ---------- */
  function track(event, data) {
    window.dataLayer = window.dataLayer || [];
    var payload = {
      event: 'plps_' + event,
      plps_unit: state.unit || '',
      plps_variant: state.variant || '',
      plps_preview: (isPreview || inDesignMode)
    };
    if (data) for (var k in data) payload['plps_' + k] = data[k];
    window.dataLayer.push(payload);
  }

  /* ---------- consent (optional) ----------
     When "Require marketing consent" is on, the popup will not open and no
     Klaviyo call is made until Shopify's Customer Privacy API reports
     marketing consent. If the API never loads, nothing shows - that is the
     conservative behaviour and it is intentional. */
  /* Is a consent banner currently on screen?
     Two signals, either one counts:
       1. Shopify's Customer Privacy API says the banner should be showing for
          this visitor (region requires it and nothing has been collected yet).
       2. A Pandectes banner element is actually visible in the DOM. This is
          the fallback for regions like the US where marketingAllowed() is true
          by default (opt-out model) so the API alone would let the popup open
          on top of the banner - which is exactly what we are preventing. */
  function consentBannerVisible() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.shouldShowBanner === 'function') {
      try { if (cp.shouldShowBanner()) return true; } catch (e) {}
    }
    var nodes = document.querySelectorAll('[id*="pandectes"], [class*="pandectes"]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var hint = (el.id + ' ' + el.className).toLowerCase();
      if (hint.indexOf('banner') === -1 && hint.indexOf('modal') === -1) continue;
      if (el.hidden) continue;
      var cs = window.getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) continue;
      var r = el.getBoundingClientRect();
      if (r.height > 0 && r.width > 0) return true;
    }
    return false;
  }

  /* ---------- CMP dismissal watcher ----------
     The Pandectes banner can be closed two ways: a real choice (Accept /
     Decline / Preferences), which fires Shopify's visitorConsentCollected,
     or the X / Escape, which fires nothing. An X-press means "not now" - so
     it suppresses the popup for the rest of the session. Detection: the
     banner was visible, then is not, and no consent event arrived within a
     grace window. The flag is sessionStorage, so a new tab or tomorrow's
     visit starts clean, matching the banner's own reappearance. */
  var CMP_DISMISS_KEY = 'plps:cmp-dismissed';
  function cmpDismissed() {
    try { return sessionStorage.getItem(CMP_DISMISS_KEY) === '1'; } catch (e) { return false; }
  }
  (function watchCmpDismiss() {
    if (!cfg.requireConsent) return;
    var sawBanner = false;
    var lastChoiceAt = 0;
    var settled = false;
    var startedAt = Date.now();
    document.addEventListener('visitorConsentCollected', function () { lastChoiceAt = Date.now(); });
    (function poll() {
      if (settled) return;
      if (consentBannerVisible()) {
        sawBanner = true;
        setTimeout(poll, 500);
        return;
      }
      if (!sawBanner) {
        /* Banner never rendered (stored consent, or region without one).
           Keep watching briefly in case it mounts late, then stand down. */
        if (Date.now() - startedAt > 30000) { settled = true; return; }
        setTimeout(poll, 500);
        return;
      }
      /* Visible -> gone. Give the consent event time to arrive before
         concluding this was a bare dismissal. */
      settled = true;
      setTimeout(function () {
        if (Date.now() - lastChoiceAt < 2500) return; // Accept/Decline/Prefs
        try { sessionStorage.setItem(CMP_DISMISS_KEY, '1'); } catch (e) {}
      }, 1200);
    })();
  })();

  /* Resolves true only when BOTH hold:
       - marketingAllowed() is true (Pandectes writes into Shopify's API), and
       - no consent banner is on screen.
     The API-readiness wait is capped at 6s and fails closed. The banner wait
     is NOT capped: if the visitor never clears the banner, the popup never
     opens on this pageview. visitorConsentCollected short-circuits the poll
     the moment they accept or decline.
     opts.ignoreBanner: API-only check, used by the form submit paths so a
     visitor who is actively submitting (preview mode can open a unit while a
     banner is up) is never left waiting on the banner. */
  function consentOk(opts) {
    if (!cfg.requireConsent) return Promise.resolve(true);
    var ignoreBanner = !!(opts && opts.ignoreBanner);
    return new Promise(function (resolve) {
      var start = Date.now();
      var settled = false;
      function finish(v) {
        if (settled) return;
        settled = true;
        document.removeEventListener('visitorConsentCollected', onConsent);
        resolve(v);
      }
      var bannerWasSeen = false;
      var bannerSettleWaited = false;
      function check() {
        if (settled) return;
        if (!ignoreBanner && cmpDismissed()) { finish(false); return; }
        var cp = window.Shopify && window.Shopify.customerPrivacy;
        var apiReady = cp && typeof cp.marketingAllowed === 'function';
        if (!apiReady) {
          if (Date.now() - start > 6000) { finish(false); return; }
          setTimeout(check, 400);
          return;
        }
        var allowed = false;
        try { allowed = !!cp.marketingAllowed(); } catch (e) { allowed = false; }
        if (!ignoreBanner && consentBannerVisible()) { bannerWasSeen = true; setTimeout(check, 500); return; }
        if (!ignoreBanner && bannerWasSeen && !bannerSettleWaited) {
          /* The banner just cleared. Wait out the dismissal watcher's grace
             window so an X-press is flagged before we conclude "allowed". */
          bannerSettleWaited = true;
          setTimeout(check, 2000);
          return;
        }
        if (allowed) { finish(true); return; }
        // Not allowed and no banner: they declined, or region blocks by default.
        finish(false);
      }
      function onConsent() { setTimeout(check, 50); }
      document.addEventListener('visitorConsentCollected', onConsent);
      check();
    });
  }

  /* ---------- known-subscriber check ----------
     Klaviyo identifies a profile when someone arrives from an email or has
     submitted a form on this browser before. Used only to skip gate units. */
  function klaviyoIdentified() {
    if (!cfg.respectKlaviyoIdentified) return Promise.resolve(false);
    return new Promise(function (resolve) {
      var start = Date.now();
      (function poll() {
        var k = window.klaviyo;
        if (k && typeof k.isIdentified === 'function') {
          try {
            var r = k.isIdentified();
            if (r && typeof r.then === 'function') {
              r.then(function (v) { resolve(!!v); }).catch(function () { resolve(false); });
            } else {
              resolve(!!r);
            }
          } catch (e) { resolve(false); }
          return;
        }
        if (Date.now() - start > 3000) { resolve(false); return; }
        setTimeout(poll, 300);
      })();
    });
  }

  /* Resolved once at boot and read inside the dwell wait. Defaults to a
     settled `false` so the preview and theme-editor paths never wait on it. */
  var identifiedPromise = Promise.resolve(false);

  /* ---------- path / UTM eligibility ---------- */
  function pathExcluded() {
    var path = location.pathname.toLowerCase();
    var list = cfg.excludedPaths || [];
    for (var i = 0; i < list.length; i++) {
      var p = String(list[i] || '').trim().toLowerCase();
      if (!p) continue;
      if (path.indexOf(p) === 0) return true;
    }
    return false;
  }

  function onCartPage() {
    return cfg.pageType === 'cart' || location.pathname.replace(/\/$/, '') === '/cart';
  }

  // Gate units (1-4) never show on the cart page or on excluded paths.
  // The cart-exit unit is exempt from the cart-page rule only.
  function pathAllowed(unit) {
    if (pathExcluded()) return false;
    if (unit !== 'cart_exit' && onCartPage()) return false;
    return true;
  }

  function utmAllowed(unit) {
    var rule = (cfg.units[unit] && cfg.units[unit].onlyUtm) || '';
    rule = String(rule).trim();
    if (!rule) return true;
    var clauses = rule.split(',');
    for (var i = 0; i < clauses.length; i++) {
      var pair = clauses[i].split('=');
      if (pair.length !== 2) continue;
      var key = pair[0].trim().toLowerCase();
      var val = pair[1].trim().toLowerCase();
      if (!key || !val) continue;
      var current = (params.get(key) || sessionUtm[key] || '').toLowerCase();
      if (current === val) return true;
    }
    return false;
  }

  /* ---------- input validation ---------- */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

  function validEmail(v) {
    return EMAIL_RE.test(String(v || '').trim());
  }

  // Klaviyo requires E.164. Normalise common US formats and anything the
  // visitor types with a leading +. Returns null when it cannot be trusted.
  function normalizePhone(raw) {
    var v = String(raw || '').trim();
    if (!v) return null;
    var hasPlus = v.charAt(0) === '+';
    var digits = v.replace(/\D/g, '');
    if (!digits) return null;
    if (hasPlus) {
      return (digits.length >= 8 && digits.length <= 15) ? '+' + digits : null;
    }
    var cc = String(cfg.defaultCountryCode || '+1').replace(/\D/g, '') || '1';
    if (cc === '1') {
      if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
      if (digits.length === 10) return '+1' + digits;
      return null;
    }
    if (digits.indexOf(cc) === 0 && digits.length >= 10) return '+' + digits;
    if (digits.length >= 6 && digits.length <= 13) return '+' + cc + digits.replace(/^0+/, '');
    return null;
  }

  /* ---------- Klaviyo client subscribe ---------- */
  function subscribe(listId, attrs, properties, source) {
    var profileAttrs = {};
    if (attrs.email) profileAttrs.email = attrs.email;
    if (attrs.phone) profileAttrs.phone_number = attrs.phone;
    if (properties && Object.keys(properties).length) profileAttrs.properties = properties;
    // DO NOT add a `subscriptions` object here. It was tried on 20 Aug 2026 and
    // the endpoint rejected it outright at revision 2024-10-15:
    //   400 invalid - "'subscriptions' is not a valid field"
    //   pointer /data/attributes/profile/data/attributes/subscriptions
    // That failure broke every submit, email included. This endpoint derives
    // the consent record from the presence of email / phone_number. Verify SMS
    // consent by inspecting the profile's SMS channel in Klaviyo after a real
    // opt-in, not by reasoning from the Subscribe Profiles docs, which describe
    // a different (server-side) endpoint.
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source,
          profile: { data: { type: 'profile', attributes: profileAttrs } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.klaviyoCompanyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
      body: JSON.stringify(body)
    }).then(function (res) {
      // fetch resolves on 4xx/5xx, so this check is what makes failures real.
      if (!res.ok) {
        var err = new Error('Klaviyo responded ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res;
    });
  }

  /* Without this the browser stays anonymous to Klaviyo onsite tracking, so
     browse-abandon and Active-on-Site never attach to the profile that just
     subscribed. This is the single biggest gap versus a hosted popup tool. */
  function klaviyoIdentify(attrs) {
    try {
      var k = window.klaviyo;
      if (!k) return;
      var payload = {};
      if (attrs.email) payload.email = attrs.email;
      if (attrs.phone) payload.phone_number = attrs.phone;
      if (!Object.keys(payload).length) return;
      if (typeof k.identify === 'function') { k.identify(payload); }
      else if (typeof k.push === 'function') { k.push(['identify', payload]); }
    } catch (e) {}
  }

  /* Back-in-stock alert for the abandoned variant. The cart-exit unit promises
     "we'll tell you before it's gone", and nothing else in this file keeps that
     promise. Variant id format matches assets/klaviyo-bis.js. */
  function backInStock(variantId, attrs, channels) {
    if (!variantId || (!attrs.email && !attrs.phone)) return Promise.resolve();
    var profileAttrs = {};
    if (attrs.email) profileAttrs.email = attrs.email;
    if (attrs.phone) profileAttrs.phone_number = attrs.phone;
    return fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(cfg.klaviyoCompanyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: channels || ['EMAIL'],
            profile: { data: { type: 'profile', attributes: profileAttrs } }
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
          }
        }
      })
    }).then(function (res) {
      if (!res.ok) throw new Error('BIS responded ' + res.status);
    }).catch(function (err) {
      // Never block the success step on this; surface it for debugging only.
      track('bis_error', { reason: (err && err.message) || 'unknown' });
    });
  }

  function klaviyoEvent(metricName, email, properties) {
    // Off by default: every new metric consumes Klaviyo metric capacity.
    // Goal data is written to the profile as `hair_goal` regardless.
    if (!cfg.trackGoalEvent || !email) return Promise.resolve();
    return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(cfg.klaviyoCompanyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: metricName } } },
            profile: { data: { type: 'profile', attributes: { email: email } } },
            properties: properties || {}
          }
        }
      })
    }).catch(function () {});
  }

  /* ---------- cart summary (for cart_exit) ---------- */
  function getCart() {
    // plCart is a display-only summary from footer-scripts; if it lacks the
    // variant id we need for back-in-stock, fall through to /cart.js.
    if (window.plCart && window.plCart.count >= 0 && window.plCart.firstItem !== undefined && window.plCart.variantId) {
      return Promise.resolve(window.plCart);
    }
    return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
      var first = (c.items && c.items[0]) || null;
      return {
        firstItem: first ? (first.product_title + (first.variant_title && first.variant_title !== 'Default Title' ? ' - ' + first.variant_title : '')) : '',
        count: c.item_count || 0,
        image: first ? first.image : '',
        qty: first ? first.quantity : 0,
        variantId: first ? String(first.variant_id) : '',
        productId: first ? String(first.product_id) : '',
        productTitle: first ? first.product_title : '',
        variantTitle: (first && first.variant_title && first.variant_title !== 'Default Title') ? first.variant_title : '',
        url: first ? first.url : ''
      };
    }).catch(function () { return { firstItem: '', count: 0 }; });
  }

  /* ---------- modal + step engine ---------- */
  var state = { unit: null, variant: '', email: '', goal: '', goalUrl: '', smsCode: '', open: false, cart: null };
  var overlay, modal, teaser = root.querySelector('[data-plps-teaser]');
  var lastFocus = null;
  var scrollLockY = 0;
  var titleSeq = 0;

  function lockScroll() {
    scrollLockY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollLockY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.setAttribute('data-plps-locked', '');
  }

  function unlockScroll() {
    if (!document.body.hasAttribute('data-plps-locked')) return;
    document.body.removeAttribute('data-plps-locked');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollLockY);
  }

  function setBackgroundInert(on) {
    var kids = document.body.children;
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (el === overlay || el === modal) continue;
      if (on) {
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('data-plps-inerted', '');
      } else if (el.hasAttribute('data-plps-inerted')) {
        el.removeAttribute('inert');
        el.removeAttribute('aria-hidden');
        el.removeAttribute('data-plps-inerted');
      }
    }
  }

  function clearBackgroundInert() {
    var marked = document.querySelectorAll('[data-plps-inerted]');
    for (var i = 0; i < marked.length; i++) {
      marked[i].removeAttribute('inert');
      marked[i].removeAttribute('aria-hidden');
      marked[i].removeAttribute('data-plps-inerted');
    }
  }

  function labelModalFromStep(stepEl) {
    if (!modal) return;
    var title = (stepEl || modal).querySelector('.plps-title');
    if (!title) return;
    if (!title.id) { titleSeq += 1; title.id = 'plps-title-' + titleSeq; }
    modal.setAttribute('aria-labelledby', title.id);
    modal.removeAttribute('aria-label');
  }

  function buildModal(unit) {
    var tpl = root.querySelector('[data-plps-unit="' + unit + '"]');
    if (!tpl) return false;
    // Safety: never allow stacked overlays/modals (e.g. rapid editor selects).
    document.querySelectorAll('.plps-overlay, .plps-modal').forEach(function (el) { el.remove(); });
    clearBackgroundInert();
    overlay = document.createElement('div');
    overlay.className = 'plps-overlay';
    modal = document.createElement('div');
    modal.className = 'plps-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Perfect Locks offer');
    modal.setAttribute('tabindex', '-1');
    // Which unit is open. Only the template's CONTENTS get cloned, so the
    // data-plps-unit attribute on <template> never reaches the DOM - without
    // this there is no way to style one unit differently from another.
    modal.setAttribute('data-plps-unit-type', unit);
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'plps-close';
    close.setAttribute('aria-label', 'Close');
    close.setAttribute('data-plps-action', 'dismiss');
    var closeColor = cfg.units[unit] && cfg.units[unit].closeColor;
    close.style.setProperty('color', closeColor === 'white' ? '#FAF7F2' : '#1C1208', 'important');
    var iconTpl = root.querySelector('[data-plps-icon-close]');
    if (iconTpl) { close.appendChild(iconTpl.content.cloneNode(true)); }
    else { close.innerHTML = '&#10005;'; }
    modal.appendChild(close);
    modal.appendChild(tpl.content.cloneNode(true));
    /* Honeypot. A field no human can see or reach - hidden from assistive
       tech, out of the tab order, offscreen, autocomplete off - that form
       bots fill anyway because it is there. A filled honeypot marks the
       submit as automated. Injected at build time so every unit's forms get
       it without touching any template. */
    modal.querySelectorAll('form[data-plps-form]').forEach(function (f) {
      if (f.querySelector('input[name="website"]')) return;
      var hp = document.createElement('input');
      hp.type = 'text';
      hp.name = 'website';
      hp.setAttribute('tabindex', '-1');
      hp.setAttribute('aria-hidden', 'true');
      hp.setAttribute('autocomplete', 'off');
      hp.style.cssText = 'position:absolute;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
      f.appendChild(hp);
    });
    document.body.appendChild(overlay);
    document.body.appendChild(modal);
    return true;
  }

  function openUnit(unit, opts) {
    if (state.open) return;
    lastFocus = document.activeElement;
    if (!buildModal(unit)) return;
    state.unit = unit;
    state.open = true;
    state.openedAt = Date.now();
    if (unit === 'cart_exit') hydrateCartFields();
    if (unit === 'game') gamePrime();
    lockScroll();
    setBackgroundInert(true);
    /* Reveal gate. Template content is inert, so an instant open (preview,
       exit intent, the showroom swap) starts every image cold and the modal
       used to paint before its hero arrived - the pop-in. Hold the reveal for
       image decode, capped at 400ms so a slow network can never make the
       popup feel slow; anything still loading after the cap fades in over
       the placeholder surface instead of popping from blank. */
    var gateImgs = Array.prototype.slice.call(
      modal.querySelectorAll('.plps-media img, .plps-bgimg img, .plps-game-stagebg img, .plps-cartline img')
    );
    gateImgs.forEach(function (im) {
      if (im.complete) { im.classList.add('plps-img-in'); return; }
      im.addEventListener('load', function () { im.classList.add('plps-img-in'); }, { once: true });
      im.addEventListener('error', function () { im.classList.add('plps-img-in'); }, { once: true });
    });
    var pending = gateImgs.filter(function (im) { return !im.complete; });
    var revealed = false;
    function reveal() {
      if (revealed) return;
      revealed = true;
      requestAnimationFrame(function () {
        overlay.classList.add('is-open');
        modal.classList.add('is-open');
      });
    }
    if (!pending.length) {
      reveal();
    } else {
      setTimeout(reveal, 400);
      Promise.all(pending.map(function (im) {
        return im.decode ? im.decode().catch(function () {}) : Promise.resolve();
      })).then(reveal, reveal);
    }
    labelModalFromStep(modal.querySelector('[data-plps-step].is-active'));
    // Focus the dialog itself rather than the first input: avoids launching
    // the mobile keyboard on open and still announces the dialog.
    modal.focus();
    if (teaser) teaser.classList.remove('is-visible');
    document.addEventListener('keydown', onKeydown);
    modal.addEventListener('click', onModalClick);
    modal.addEventListener('submit', onSubmit);
    overlay.addEventListener('click', function () { dismiss(); });
    track('impression', { trigger: (opts && opts.trigger) || 'gate' });
  }

  function closeModal() {
    if (!state.open) return;
    state.open = false;
    document.removeEventListener('keydown', onKeydown);
    if (overlay) overlay.remove();
    if (modal) modal.remove();
    overlay = modal = null;
    setBackgroundInert(false);
    clearBackgroundInert();
    unlockScroll();
    if (lastFocus && document.contains(lastFocus) && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    }
    lastFocus = null;
  }

  function dismiss() {
    track('dismiss');
    if (!isPreview && !inDesignMode) suppress(state.unit);
    closeModal();
  }

  function showStep(name) {
    var steps = modal.querySelectorAll('[data-plps-step]');
    for (var i = 0; i < steps.length; i++) steps[i].classList.remove('is-active');
    var next = modal.querySelector('[data-plps-step="' + name + '"]');
    if (next) {
      next.classList.add('is-active');
      labelModalFromStep(next);
      var f = next.querySelector('input, button, a');
      if (f) f.focus();
      track('step', { step: name });
    }
  }

  /* ---------- inline errors ---------- */
  function clearError(form) {
    var box = form.querySelector('[data-plps-error]');
    if (box) { box.textContent = ''; box.hidden = true; }
    var field = form.querySelector('.plps-field');
    if (field) field.removeAttribute('aria-invalid');
  }

  function showError(form, message) {
    var box = form.querySelector('[data-plps-error]');
    var field = form.querySelector('.plps-field');
    if (field) {
      field.setAttribute('aria-invalid', 'true');
      try { field.focus(); } catch (e) {}
    }
    if (box) {
      box.hidden = false;
      box.textContent = message;
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { dismiss(); return; }
    if (e.key === 'Tab' && modal) {
      // Focus trap - backstop for browsers without inert support.
      var items = modal.querySelectorAll('input, button, a, [tabindex]');
      var list = [];
      for (var i = 0; i < items.length; i++) {
        if (items[i].offsetParent !== null || items[i] === modal) list.push(items[i]);
      }
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === modal)) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
  }

  function setCode(code) {
    var els = modal.querySelectorAll('[data-plps-code]');
    for (var i = 0; i < els.length; i++) els[i].textContent = code;
  }

  function hydrateCartFields() {
    getCart().then(function (cart) {
      state.cart = cart;
      if (!modal) return;
      var names = modal.querySelectorAll('[data-plps-cart-name]');
      for (var i = 0; i < names.length; i++) names[i].textContent = cart.firstItem || 'Your shade';
      var qty = modal.querySelector('[data-plps-cart-qty]');
      if (qty) qty.textContent = cart.count ? (cart.count + (cart.count === 1 ? ' item' : ' items') + ' in your cart') : '';
      var img = modal.querySelector('[data-plps-cart-image]');
      if (img) {
        if (cart.image) { img.src = cart.image; } else { img.style.display = 'none'; }
      }
      /* Personalized headline: the returning unit converts at 20x the intro
         units by acknowledging the visitor, so the cart save names the shade
         they actually chose. Falls back to the generic headline whenever the
         cart has no usable name. */
      var ph = modal.querySelector('[data-plps-exit-headline][data-personalized]');
      if (ph && (cart.variantTitle || cart.productTitle || cart.firstItem)) {
        var shade = cart.variantTitle || cart.productTitle || cart.firstItem || '';
        var product = cart.productTitle || cart.firstItem || shade;
        var line = ph.getAttribute('data-personalized')
          .replace('{shade}', shade)
          .replace('{product}', product);
        if (line.replace(/\s/g, '')) ph.textContent = line;
      }
    });
  }

  function onModalClick(e) {
    var btn = e.target.closest('[data-plps-action]');
    if (!btn) return;
    var action = btn.getAttribute('data-plps-action');

    if (action === 'dismiss') { dismiss(); }

    else if (action === 'stylist') {
      LS.set('stylist', '1');
      CK.set('stylist', '1', 365);
      isStylist = true;
      track('handraise');
      var proTpl = root.querySelector('[data-plps-unit="pro"]');
      closeModal();
      if (proTpl) openUnit('pro', { trigger: 'handraise' });
    }

    else if (action === 'not-stylist') {
      LS.set('stylist', '0');
      CK.set('stylist', '0', 365);
      isStylist = false;
      dismiss();
    }

    else if (action === 'goal') {
      state.goal = btn.getAttribute('data-goal') || '';
      state.goalUrl = btn.getAttribute('data-url') || '';
      var echo = modal.querySelector('[data-plps-goal-echo]');
      if (echo) echo.textContent = (btn.querySelector('b') ? btn.querySelector('b').textContent : 'Good choice') + ' it is';
      track('goal', { goal: state.goal });
      showStep('email');
    }

    else if (action === 'redeem') {
      e.preventDefault();
      var url = btn.hasAttribute('data-plps-goal-url')
        ? (state.goalUrl || '/collections/all')
        : (btn.getAttribute('data-url') || '/');
      var code = state.smsCode || (cfg.units[state.unit] && cfg.units[state.unit].codeEmail);
      track('redeem');
      if (!isPreview && !inDesignMode) suppress(state.unit);
      unlockScroll();
      location.href = code ? ('/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(url)) : url;
    }

    else if (action === 'apply-redirect') {
      e.preventDefault();
      var applyUrl = btn.getAttribute('data-url') || '/';
      var joiner = applyUrl.indexOf('?') > -1 ? '&' : '?';
      track('apply');
      if (!isPreview && !inDesignMode) suppress(state.unit);
      unlockScroll();
      location.href = applyUrl + (state.email ? joiner + 'email=' + encodeURIComponent(state.email) : '');
    }

    else if (action === 'copy') {
      e.preventDefault();
      var chip = btn.closest('.plps-code');
      var codeEl = chip && chip.querySelector('[data-plps-code]');
      var label = btn.querySelector('[data-plps-copy-label]');
      var text = codeEl ? (codeEl.textContent || '').trim() : '';
      if (!text) return;
      var done = function () {
        btn.setAttribute('data-copied', '1');
        if (label) label.textContent = 'Copied';
        track('code_copy');
        setTimeout(function () {
          btn.removeAttribute('data-copied');
          if (label) label.textContent = 'Copy';
        }, 2400);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(function () { fallbackCopy(text, done); });
      } else {
        fallbackCopy(text, done);
      }
    }

    else if (action === 'back') {
      e.preventDefault();
      var target = btn.getAttribute('data-step');
      if (!target || !modal.querySelector('[data-plps-step="' + target + '"]')) return;
      // The goal is only sent to Klaviyo on submit, so clearing it here is safe
      // and stops a stale pick leaking into the profile if they change their mind.
      if (target === 'goal') { state.goal = ''; state.goalUrl = ''; }
      track('back', { step: target });
      showStep(target);
    }

    else if (action === 'skip-sms') {
      track('sms_skip');
      showStep('success');
    }

    else if (action === 'link') {
      track('cta');
      if (!isPreview && !inDesignMode) suppress(state.unit);
      unlockScroll();
      // natural navigation proceeds
    }
    else if (action === 'colormatch') {
      e.preventDefault();
      track('cta');
      closeModal();
      openColorMatch(btn.getAttribute('data-url') || btn.getAttribute('href') || '/pages/find-your-perfect-match');
    }
  }

  /* The color-match widget is mounted on the PDP and collection templates,
     which is where the returning unit fires. When its launcher is on the
     page, open the drawer right here and hand it the email the popup just
     captured - one continuous flow, nothing asked twice. When it is not
     (home, blog), navigate to the match page with the email carried in the
     URL. Returns true when the in-place drawer path was taken. */
  function openColorMatch(dest) {
    /* Detect the widget by its email field, which exists in every mode; the
       launcher button is removable via its own setting and cannot be the
       test. Opening goes through the widget's own pl-cmw:open event, the
       same API its launcher links use, carrying the email. The email in hand is
       what tells the widget to skip its intro, so fall back to whatever is
       still typed in the popup's field if state was never set. */
    var handEmail = state.email || '';
    if (!handEmail && modal) {
      var lastField = modal.querySelector('input[name="email"]');
      if (lastField && lastField.value) handEmail = String(lastField.value).trim();
    }
    if (document.querySelector('[data-cmw-email]')) {
      window.setTimeout(function () {
        document.dispatchEvent(new CustomEvent('pl-cmw:open', { detail: { email: handEmail } }));
      }, 60);
      return true;
    }
    var joiner = dest.indexOf('?') > -1 ? '&' : '?';
    var parts = ['cm_open=1'];
    if (handEmail) parts.push('email=' + encodeURIComponent(handEmail));
    var navTo = dest + joiner + parts.join('&');
    /* Same rule as the handoff path: never navigate in the same tick as the
       analytics events. An instant location change killed the returning
       unit's email_submit hits in flight - Klaviyo saw the captures, GA4
       saw zero. A quarter second is invisible next to a page load. */
    window.setTimeout(function () { window.location.href = navTo; }, 250);
    return false;
  }

  /* clipboard API needs a secure context and can be blocked; this keeps the
     button working on http previews and older browsers. */
  function fallbackCopy(text, done) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      done();
    } catch (err) {}
  }

  function onSubmit(e) {
    var form = e.target.closest('[data-plps-form]');
    if (!form) return;
    e.preventDefault();
    var kind = form.getAttribute('data-plps-form');
    var btn = form.querySelector('button[type="submit"]');
    clearError(form);

    /* Theme editor: advance steps visually, never call Klaviyo. */
    if (inDesignMode) {
      if (kind === 'email') {
        /* Preview never subscribes, but it must still behave like the real
           flow: keep the typed email so downstream handoffs carry it. */
        var pvEmail = form.querySelector('input[name="email"]');
        if (pvEmail && pvEmail.value) state.email = String(pvEmail.value).trim();
        if (state.unit === 'pro') showStep('success');
        else if (state.unit === 'game' && modal.querySelector('[data-plps-step="scratch"]')) { showStep('scratch'); gameActivate(); }
        else if (state.unit === 'returning' && document.querySelector('[data-cmw-email]')) {
          closeModal();
          openColorMatch((cfg.units.returning && cfg.units.returning.ctaUrl) || '/pages/find-your-perfect-match');
        }
        else if (modal.querySelector('[data-plps-step="sms"]')) showStep('sms');
        else showStep('success');
      } else {
        var previewCode = cfg.units[state.unit] && cfg.units[state.unit].codeSms;
        if (previewCode) setCode(previewCode);
        showStep('success');
      }
      return;
    }

    /* Automated-submit check. Two signals, either one is enough: the
       honeypot has a value, or the email form was submitted faster than a
       person can read a headline and type an address (under 1.5s from the
       modal opening). Fail silently - advance the steps as if it worked so
       the bot cannot tell it was filtered - but make no Klaviyo call. */
    var hpField = form.querySelector('input[name="website"]');
    var tooFast = kind === 'email' && state.openedAt && (Date.now() - state.openedAt) < 1500;
    if ((hpField && hpField.value) || tooFast) {
      track('bot_block', { kind: kind, reason: (hpField && hpField.value) ? 'honeypot' : 'timing' });
      if (kind === 'email') {
        if (modal.querySelector('[data-plps-step="sms"]')) showStep('sms');
        else showStep('success');
      } else {
        showStep('success');
      }
      return;
    }

    if (kind === 'email') {
      var emailRaw = (form.querySelector('input[name="email"]') || {}).value || '';
      var email = String(emailRaw).trim();
      if (!validEmail(email)) {
        showError(form, 'That email address does not look right. Please check it and try again.');
        track('validation_error', { field: 'email' });
        return;
      }
      if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
      state.email = email;
      var props = { popup_unit: state.unit, popup_variant: state.variant || '' };
      // The exact code this visitor was handed on screen. The Klaviyo welcome
      // flow branches on popup_unit and merge-tags popup_code, so the number in
      // the email always matches the number in the popup. Without this the flow
      // has to mint its own code and the two contradict each other.
      var emailCode = cfg.units[state.unit] && cfg.units[state.unit].codeEmail;
      if (emailCode) props.popup_code = emailCode;
      if (state.goal) props.hair_goal = state.goal;
      if (state.unit === 'pro') props.is_pro_prospect = true;
      // Give the abandonment flow something to name. Without these the email
      // cannot reference the shade the visitor actually left behind.
      if (state.unit === 'cart_exit' && state.cart) {
        if (state.cart.productTitle) props.cart_product = state.cart.productTitle;
        if (state.cart.variantTitle) props.cart_shade = state.cart.variantTitle;
        if (state.cart.variantId) props.cart_variant_id = state.cart.variantId;
        if (state.cart.url) props.cart_url = state.cart.url;
        if (state.cart.count) props.cart_item_count = state.cart.count;
      }

      consentOk({ ignoreBanner: true }).then(function (allowed) {
        if (!allowed) throw new Error('consent');
        return subscribe(cfg.emailListId, { email: email }, props, 'Popup ' + state.unit);
      }).then(function () {
        markSubscribed();
        klaviyoIdentify({ email: email });
        if (state.unit === 'cart_exit' && state.cart && state.cart.variantId) {
          backInStock(state.cart.variantId, { email: email }, ['EMAIL']);
        }
        track('email_submit');
        if (state.goal) {
          klaviyoEvent('Popup Goal Selected', email, { goal: state.goal, unit: state.unit });
        }
        if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
        // A form carrying data-plps-redirect hands off to another page instead of
        // advancing a step. Used by the returning unit, which captures the email
        // and then sends the visitor to the colour-match uploader with it
        // prefilled. The profile is created either way, so a visitor who never
        // finishes the upload is no longer lost entirely.
        var handoff = form.getAttribute('data-plps-redirect');
        if (handoff) {
          track('handoff');
          var sep = handoff.indexOf('?') > -1 ? '&' : '?';
          var dest = handoff + sep + 'email=' + encodeURIComponent(email);
          /* Navigation one beat later, not immediately: an instant
             location change can cancel the analytics hits in flight, which
             made handoff conversions invisible in GA4 while Klaviyo showed
             them fine. A quarter second is imperceptible next to a page
             load and lets the events clear. */
          setTimeout(function () { window.location.href = dest; }, 250);
          return;
        }
        if (state.unit === 'pro') showStep('success');
        else if (state.unit === 'game' && modal.querySelector('[data-plps-step="scratch"]')) { showStep('scratch'); gameActivate(); }
        else if (state.unit === 'returning' && document.querySelector('[data-cmw-open]')) {
          /* The pitch this visitor just accepted IS the photo flow, so keep
             the promise immediately: close the popup and open the drawer
             with their email already in it. Pages without the widget fall
             through to the success screen, whose button navigates instead. */
          track('handoff');
          closeModal();
          openColorMatch((cfg.units.returning && cfg.units.returning.ctaUrl) || '/pages/find-your-perfect-match');
        }
        else if (modal.querySelector('[data-plps-step="sms"]')) showStep('sms');
        else showStep('success');
      }).catch(function (err) {
        if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
        track('submit_error', { kind: 'email', reason: (err && err.message) || 'unknown' });
        showError(form, 'We could not save that just now. Please try again in a moment.');
      });
    }

    if (kind === 'sms') {
      var phoneRaw = (form.querySelector('input[name="phone"]') || {}).value || '';
      var phone = normalizePhone(phoneRaw);
      if (!phone) {
        showError(form, 'Please enter a valid mobile number, for example (510) 555-1234.');
        track('validation_error', { field: 'phone' });
        return;
      }
      if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
      var smsProps = { popup_unit: state.unit, popup_variant: state.variant || '' };
      // Overwrites popup_code from the email step. Reaching this step means the
      // visitor upgraded, so the SMS-tier code is the one they were promised.
      var smsTierCode = cfg.units[state.unit] && cfg.units[state.unit].codeSms;
      if (smsTierCode) smsProps.popup_code = smsTierCode;
      if (state.unit === 'cart_exit') {
        smsProps.shade_watch = true;
        if (state.cart && state.cart.variantId) smsProps.cart_variant_id = state.cart.variantId;
        if (state.cart && state.cart.productTitle) smsProps.cart_product = state.cart.productTitle;
      }

      consentOk({ ignoreBanner: true }).then(function (allowed) {
        if (!allowed) throw new Error('consent');
        return subscribe(cfg.smsListId, { email: state.email, phone: phone }, smsProps, 'Popup ' + state.unit + ' SMS');
      }).then(function () {
        klaviyoIdentify({ email: state.email, phone: phone });
        if (state.unit === 'cart_exit' && state.cart && state.cart.variantId) {
          backInStock(state.cart.variantId, { email: state.email, phone: phone }, ['EMAIL', 'SMS']);
        }
        track('sms_submit');
        if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
        var smsCode = cfg.units[state.unit] && cfg.units[state.unit].codeSms;
        if (smsCode) { state.smsCode = smsCode; setCode(smsCode); }
        showStep('success');
      }).catch(function (err) {
        if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
        track('submit_error', { kind: 'sms', reason: (err && err.message) || 'unknown' });
        showError(form, 'We could not sign you up for texts just now. Your email code is still good - close this and check your inbox.');
      });
    }
  }

  /* ---------- Unit 7: scratch game ---------- */
  /* The card ships inert: cream prize face, canvas foil on top, lock overlay
     explaining the email gate. gameActivate() runs after the email submit -
     it lifts the lock, paints the foil, and wires the scratch. Painting waits
     for a frame because the scratch step has only just become visible and the
     canvas needs real layout dimensions. "Reveal it for me" is the keyboard,
     switch and screen-reader path and also catches impatient thumbs; both
     routes land in the same complete() so the prize never depends on motor
     ability. Clearing 55% of the foil counts as done - nobody should have to
     sand the whole card. */
  var gameRevealed = false;
  function gamePaintFoil(canvas, ctx, bandPos, withText) {
    var r = canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var pw = Math.round(r.width * dpr);
    if (canvas.width !== pw) { canvas.width = pw; canvas.height = Math.round(r.height * dpr); }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    var w = r.width, h = r.height;
    var g = ctx.createLinearGradient(0, 0, w, h * 0.9);
    g.addColorStop(0, '#CFA265');
    g.addColorStop(0.28, '#B8874A');
    g.addColorStop(0.5, '#E2C48E');
    g.addColorStop(0.62, '#A97C40');
    g.addColorStop(0.85, '#C89A5C');
    g.addColorStop(1, '#B8874A');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    var seed = 9;
    function rnd() { seed = (seed * 16807) % 2147483647; return seed / 2147483647; }
    ctx.lineWidth = 1;
    for (var i = 0; i < 90; i++) {
      var y0 = rnd() * (h + 40) - 20;
      var x0 = rnd() * w;
      var len = 30 + rnd() * 90;
      var tone = rnd();
      ctx.strokeStyle = tone < 0.5
        ? 'rgba(255,240,210,' + (0.05 + tone * 0.08).toFixed(3) + ')'
        : 'rgba(90,62,25,' + (0.04 + (tone - 0.5) * 0.08).toFixed(3) + ')';
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x0 + len, y0 - len * 0.18);
      ctx.stroke();
    }
    if (bandPos !== undefined) {
      var bx = bandPos * (w + h);
      var sg = ctx.createLinearGradient(bx - 70, 0, bx + 70, h * 0.35);
      sg.addColorStop(0, 'rgba(255,248,230,0)');
      sg.addColorStop(0.5, 'rgba(255,248,230,0.28)');
      sg.addColorStop(1, 'rgba(255,248,230,0)');
      ctx.fillStyle = sg;
      ctx.fillRect(0, 0, w, h);
    }
    /* The engraving only belongs on a card that can actually be scratched.
       The locked state paints bare gold - the lock overlay carries the one
       message that matters there. */
    if (withText !== false) {
      ctx.textAlign = 'center';
      ctx.font = '600 12px "Brandon Grotesque", sans-serif';
      ctx.fillStyle = 'rgba(255,240,210,0.85)';
      ctx.fillText('S C R A T C H   T O   R E V E A L', w / 2, h / 2 + 5);
      ctx.fillStyle = '#6E521F';
      ctx.fillText('S C R A T C H   T O   R E V E A L', w / 2, h / 2 + 4);
    }
  }
  /* Paint the foil the moment the unit opens, while the card is still locked.
     Without this the canvas is transparent on the email step and the prize
     face shows straight through the lock scrim - the reveal spoiled before
     the email is given, and the lock text tangled with the prize text. A
     locked GOLD card is also simply the promise the headline makes. */
  function gamePrime() {
    if (!modal || state.unit !== 'game') return;
    var canvas = modal.querySelector('[data-plps-game-foil]');
    if (!canvas || !canvas.getContext) return;
    var ctx = canvas.getContext('2d');
    requestAnimationFrame(function () { gamePaintFoil(canvas, ctx, undefined, false); });
  }
  function gameActivate() {
    if (!modal || state.unit !== 'game') return;
    gameRevealed = false;
    var canvas = modal.querySelector('[data-plps-game-foil]');
    var lock = modal.querySelector('[data-plps-game-lock]');
    var hint = modal.querySelector('[data-plps-game-hint]');
    var revealBtn = modal.querySelector('[data-plps-game-reveal]');
    var live = modal.querySelector('[data-plps-game-live]');
    if (!canvas || !canvas.getContext) {
      if (modal.querySelector('[data-plps-step="sms"]')) showStep('sms');
      else showStep('success');
      return;
    }
    var ctx = canvas.getContext('2d');
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (lock) lock.hidden = true;
    if (revealBtn) revealBtn.hidden = false;
    if (hint) hint.textContent = 'Scratch the card to reveal your offer.';
    if (live) live.textContent = 'Card unlocked. Scratch the card, or use the Reveal it for me button.';
    /* showStep found nothing to focus in the scratch panel - its interactive
       elements live in the stage column. Focus the reveal button so keyboard
       users land on the accessible path, and so the browser scrolls the card
       into view on stacked mobile, where it sits above the form. */
    if (revealBtn) { try { revealBtn.focus(); } catch (e) {} }
    var cardEl = canvas.closest ? canvas.closest('.plps-game-card') : null;
    if (cardEl) cardEl.classList.add('is-armed');

    /* Foil rendering. Brushed diagonal strands over a six-stop gradient read
       as metal rather than flat ochre; the lettering is drawn twice, light
       pass then ink pass, for an engraved look. Until the first touch a
       specular band sweeps the card - the shimmer is the invitation - then
       stops forever, because each shimmer frame repaints the whole foil and
       would heal any scratches. The strand pattern is seeded, not random, so
       shimmer repaints are stable instead of crawling. */
    var interacted = false;
    var shimmerPos = -0.35;
    var shimmerRaf = 0;
    function drawFoil(bandPos) { gamePaintFoil(canvas, ctx, bandPos); }
    function shimmer() {
      if (interacted || gameRevealed) return;
      shimmerPos += 0.008;
      if (shimmerPos > 1.35) shimmerPos = -0.35;
      drawFoil(shimmerPos);
      shimmerRaf = requestAnimationFrame(shimmer);
    }
    requestAnimationFrame(function () {
      drawFoil();
      if (!reduced) shimmerRaf = requestAnimationFrame(shimmer);
    });

    var scratching = false;
    var strokes = 0;
    function scratchAt(x, y) {
      var r = canvas.getBoundingClientRect();
      var px = x - r.left, py = y - r.top;
      /* Feathered eraser: solid core, soft rim. Reads as a thumb rubbing
         foil away rather than a cursor punching holes. */
      var brush = ctx.createRadialGradient(px, py, 6, px, py, 26);
      brush.addColorStop(0, 'rgba(0,0,0,1)');
      brush.addColorStop(0.7, 'rgba(0,0,0,1)');
      brush.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = brush;
      ctx.beginPath();
      ctx.arc(px, py, 26, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
      strokes++;
      if (strokes % 8 === 0) checkCleared();
    }
    function checkCleared() {
      var img;
      try { img = ctx.getImageData(0, 0, canvas.width, canvas.height).data; } catch (e) { return; }
      var clear = 0, total = 0;
      for (var i = 3; i < img.length; i += 96) {
        total++;
        if (img[i] === 0) clear++;
      }
      if (total > 0 && clear / total >= 0.55) complete();
    }
    function complete() {
      if (gameRevealed) return;
      gameRevealed = true;
      canvas.classList.add('is-cleared');
      if (reduced) canvas.style.display = 'none';
      if (hint) hint.textContent = '';
      if (revealBtn) revealBtn.hidden = true;
      if (live) live.textContent = 'Offer revealed.';
      track('game_reveal');
      setTimeout(function () {
        if (!modal) return;
        if (modal.querySelector('[data-plps-step="sms"]')) showStep('sms');
        else showStep('success');
      }, reduced ? 0 : 650);
    }

    canvas.addEventListener('pointerdown', function (e) {
      if (gameRevealed) return;
      if (!interacted) {
        interacted = true;
        if (shimmerRaf) cancelAnimationFrame(shimmerRaf);
        drawFoil();
      }
      if (navigator.vibrate) { try { navigator.vibrate(8); } catch (err) {} }
      scratching = true;
      try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
      scratchAt(e.clientX, e.clientY);
    });
    canvas.addEventListener('pointermove', function (e) {
      if (scratching && !gameRevealed) scratchAt(e.clientX, e.clientY);
    });
    canvas.addEventListener('pointerup', function () { scratching = false; });
    canvas.addEventListener('pointercancel', function () { scratching = false; });
    if (revealBtn) revealBtn.addEventListener('click', complete);
  }

  /* ---------- Unit 8: showroom geo gate ---------- */
  /* Optional endpoint (a Cloudflare Worker reading CDN geo headers) returning
     {"show": true}. Blank endpoint means no geo restriction. Fails closed on
     error or a slow response: a wrong-region invite costs trust, a missed
     one costs nothing - the subscriber slot was empty before this unit. */
  var geoCache = null;
  function showroomGeoOk() {
    var u = cfg.units.showroom && cfg.units.showroom.geoUrl;
    if (!u) return Promise.resolve(true);
    if (geoCache) return geoCache;
    geoCache = new Promise(function (resolve) {
      var done = false;
      var t = setTimeout(function () { if (!done) { done = true; resolve(false); } }, 1500);
      try {
        fetch(u, { credentials: 'omit' }).then(function (r) { return r.json(); }).then(function (j) {
          if (!done) { done = true; clearTimeout(t); resolve(!!(j && j.show)); }
        }).catch(function () { if (!done) { done = true; clearTimeout(t); resolve(false); } });
      } catch (e) {
        if (!done) { done = true; clearTimeout(t); resolve(false); }
      }
    });
    return geoCache;
  }

  /* ---------- targeting: which unit does this visitor get? ---------- */
  function pickGateUnit() {
    var has = function (u) {
      return !!root.querySelector('[data-plps-unit="' + u + '"]') &&
        !!cfg.units[u] &&
        pathAllowed(u) &&
        utmAllowed(u);
    };

    /* Promo takeover: while the promo unit is enabled and not past its end
       date, it wins the gate slot for everyone - subscribers and stylists
       included - so Units 1-4 stand down without touching their toggles.
       Cart exit is armed separately and keeps running. */
    if (has('promo') && !suppressed('promo') && !promoExpired()) {
      state.variant = 'promo';
      return 'promo';
    }

    if (isStylist && has('pro') && !suppressed('pro')) { state.variant = 'pro'; return 'pro'; }
    if (isSubscribed) {
      /* The subscriber slot. Every welcome unit suppresses known subscribers;
         the showroom invite is the one unit built FOR them - it asks for
         nothing they have already given. */
      if (has('showroom') && !suppressed('showroom')) { state.variant = 'showroom'; return 'showroom'; }
      return null;
    }

    if (has('returning') && !suppressed('returning') &&
        sessions >= (cfg.units.returning.minSessions || 2) && LS.get('seen_intro') === '1') {
      state.variant = 'returning';
      return 'returning';
    }

    /* Intro roll. Deterministic on the visitor id so the same person always
       lands in the same arm. Two arms honour the A/B slider; the moment the
       scratch game is enabled the roll becomes equal thirds and the slider is
       ignored - a three-way test with a weighted two-way knob would silently
       misallocate traffic. */
    var hash = 0;
    for (var i = 0; i < visitorId.length; i++) hash = (hash * 31 + visitorId.charCodeAt(i)) >>> 0;

    var arms = [];
    if (has('new_visitor')) arms.push({ unit: 'new_visitor', variant: 'A_new_visitor' });
    if (has('quiz')) arms.push({ unit: 'quiz', variant: 'B_quiz' });
    if (has('game')) arms.push({ unit: 'game', variant: 'C_game' });
    if (!arms.length) return null;

    var pick;
    if (arms.length === 2 && !has('game')) {
      pick = (hash % 100) < cfg.abSplitPercent ? arms[0] : arms[1];
    } else {
      pick = arms[hash % arms.length];
    }
    if (has(pick.unit) && !suppressed(pick.unit)) {
      state.variant = pick.variant;
      return pick.unit;
    }
    return null;
  }

  /* ---------- engagement gate: first scroll + dwell ---------- */
  function armGate() {
    var unit = pickGateUnit();
    if (!unit) return;

    window.addEventListener('scroll', function () {
      warmUnit(unit);
      var remaining = Math.max(0, cfg.dwellMs - performance.now());
      setTimeout(function () {
        if (state.open || suppressed(unit) || !pathAllowed(unit)) return;
        Promise.all([consentOk(), identifiedPromise]).then(function (results) {
          var allowed = results[0];
          var identified = results[1];
          if (!allowed || state.open) return;
          whenOverlaysClear(function () {
          if (state.open || suppressed(unit) || !pathAllowed(unit) || cmpDismissed()) return;
          /* A known subscriber only ever qualifies for the pro unit or the
             promo takeover; every other unit is suppressed for them in
             pickGateUnit(). */
          if (identified && unit !== 'pro' && unit !== 'promo' && unit !== 'showroom') {
            var sw = !!root.querySelector('[data-plps-unit="showroom"]') && cfg.units.showroom &&
              pathAllowed('showroom') && utmAllowed('showroom') && !suppressed('showroom');
            if (!sw) return;
            unit = 'showroom';
            state.variant = 'showroom';
            warmUnit('showroom');
          }
          if (unit === 'new_visitor' || unit === 'quiz' || unit === 'game') LS.set('seen_intro', '1');
          if (unit === 'showroom') {
            showroomGeoOk().then(function (ok) {
              if (ok && !state.open) openUnit('showroom', { trigger: 'gate' });
            });
            return;
          }
          if (teaser && (unit === 'new_visitor' || unit === 'quiz' || unit === 'game')) {
            teaser.hidden = false;
            teaser.classList.add('is-visible');
            teaser.addEventListener('click', function () { openUnit(unit, { trigger: 'teaser' }); }, { once: true });
            state.unit = unit; // attribute the teaser event before any modal exists
            track('teaser');
            if (cfg.autoOpenMs > 0) {
              setTimeout(function () {
                if (!state.open && teaser.classList.contains('is-visible')) openUnit(unit, { trigger: 'auto' });
              }, cfg.autoOpenMs);
            }
          } else {
            openUnit(unit, { trigger: 'gate' });
          }
          });
        });
      }, remaining);
    }, { once: true, passive: true });
  }

  /* Images live inside <template>, so the browser treats them as inert and
     fetches nothing until the unit is cloned in. That keeps the system free on
     page load, at the cost of a visible pop-in the moment the modal opens.
     Warming the URL as soon as a unit becomes eligible closes that gap: the
     fetch happens during the dwell wait, so by the time it opens the image is
     already in cache. Called at most once per unit. */
  var warmed = {};
  function warmUnit(unit) {
    if (!unit || warmed[unit]) return;
    warmed[unit] = true;
    try {
      var tpl = root.querySelector('[data-plps-unit="' + unit + '"]');
      if (!tpl || !tpl.content) return;
      /* Clone the media into a live offscreen container instead of guessing
         URLs. The browser then runs its own source selection - media query,
         srcset, sizes, device pixel ratio - and fetches the exact file the
         modal's clone will request, so the cache hit is guaranteed. The old
         approach warmed the first srcset candidate (600w), which a 2x
         display never requests: it resolves the 320px slot to 640px and
         fetches the 1000w file, missing the cache on every retina desktop. */
      var nodes = tpl.content.querySelectorAll('picture, img');
      if (!nodes.length) return;
      var warmer = document.createElement('div');
      warmer.setAttribute('aria-hidden', 'true');
      warmer.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);pointer-events:none;';
      nodes.forEach(function (el) {
        if (el.tagName === 'IMG' && el.parentNode && el.parentNode.tagName === 'PICTURE') return;
        warmer.appendChild(el.cloneNode(true));
      });
      document.body.appendChild(warmer);
      setTimeout(function () {
        if (warmer.parentNode) warmer.parentNode.removeChild(warmer);
      }, 15000);
    } catch (e) {}
  }

  /* ---------- desktop exit intent for cart_exit ---------- */
  /* Something else already owns the screen: the mini-cart, a modal, a menu.
     The theme's OpenableElement marks these with an `open` attribute. Firing
     exit intent over an open drawer reads as an ambush, because the visitor
     is mid-task rather than leaving. */
  function overlayOpen() {
    if (document.querySelector('cart-drawer[open], [id="mini-cart"][open]')) return true;
    if (document.querySelector('.drawer[open], .modal[open], dialog[open]')) return true;
    // Match bar bottom sheets (mobile) - the visitor is mid-shade-picking.
    if (document.querySelector('.pl-mb-panel.is-open')) return true;
    return false;
  }

  /* Wait until no drawer/sheet is open, then run cb. Polls because the theme's
     overlays have no shared close event. Capped at 90s so an abandoned tab
     doesn't hold a timer forever; if still blocked by then, this pageview
     simply gets no popup - the next one starts fresh. */
  function whenOverlaysClear(cb) {
    var waited = 0;
    (function poll() {
      if (!overlayOpen()) { cb(); return; }
      waited += 1000;
      if (waited >= 90000) return;
      setTimeout(poll, 1000);
    })();
  }

  /* Adding to cart is the opposite of leaving. A mouseout in the seconds after
     it is almost always the pointer travelling to the drawer that just opened,
     so hold fire and let the visitor finish what they started. */
  var lastCartChange = 0;
  ['cart:updated', 'cart:refresh', 'variant:added', 'product:added'].forEach(function (evt) {
    document.addEventListener(evt, function () { lastCartChange = Date.now(); });
  });
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest && e.target.closest('[name="add"], [data-action="add-to-cart"], form[action*="/cart/add"] [type="submit"]');
    if (t) lastCartChange = Date.now();
  }, true);
  var CART_GRACE_MS = 10000;

  function armExitIntent() {
    if (!root.querySelector('[data-plps-unit="cart_exit"]') || !cfg.units.cart_exit) return;
    if (!pathAllowed('cart_exit') || !utmAllowed('cart_exit')) return;
    if (suppressed('cart_exit')) return;
    var armedAt = Date.now();
    var fired = false;
    getCart().then(function (cart) { if (cart && cart.count) warmUnit('cart_exit'); });

    function tryFire(trigger, cleanup) {
      if (fired || state.open) return;
      if (overlayOpen()) return;
      if (Date.now() - lastCartChange < CART_GRACE_MS) return;
      if (Date.now() - armedAt < 5000) return;
      consentOk().then(function (allowed) {
        if (!allowed || fired || state.open) return;
        return getCart().then(function (cart) {
          if (fired || state.open || !cart.count) return;
          fired = true;
          if (cleanup) cleanup();
          state.variant = 'cart_exit';
          openUnit('cart_exit', { trigger: trigger });
        });
      });
    }

    if (window.matchMedia('(pointer: fine)').matches) {
      var handler = function (e) {
        if (fired || state.open) return;
        if (e.clientY > 0 || e.relatedTarget) return;
        tryFire('exit', function () { document.removeEventListener('mouseout', handler); });
      };
      document.addEventListener('mouseout', handler);
      return;
    }

    /* Mobile back-intent. A fast flick toward the top after real browsing
       depth reads as reaching for the URL bar or the back gesture, which is
       the closest thing touch has to mouse-out. Thresholds are deliberately
       strict - 350px of upward travel inside half a second, only after 600px
       of depth, and never when already near the top - because a misfire here
       is an ambush mid-read, and an ambush costs more trust than the save is
       worth. One shot per page, same cart and consent guards as desktop. */
    var lastY = window.scrollY;
    var lastT = Date.now();
    var upDist = 0;
    var maxDepth = 0;
    var onScroll = function () {
      if (fired || state.open) return;
      var y = window.scrollY;
      var t = Date.now();
      if (y > maxDepth) maxDepth = y;
      if (y < lastY) {
        if (t - lastT <= 500) upDist += lastY - y; else upDist = lastY - y;
      } else {
        upDist = 0;
      }
      lastY = y;
      lastT = t;
      if (upDist >= 350 && maxDepth >= 600 && y > 100) {
        upDist = 0;
        tryFire('exit_mobile', function () { window.removeEventListener('scroll', onScroll); });
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- boot ---------- */
  if (inDesignMode) {
    /* Theme editor integration: selecting a unit block in the customizer opens
       that unit immediately - no gates, no suppression - and deselecting closes
       it. Forms advance through their steps on submit WITHOUT calling Klaviyo,
       so every screen can be reviewed by clicking through it. */
    document.addEventListener('shopify:block:select', function (e) {
      var tpl = e.target && (e.target.matches('[data-plps-unit]') ? e.target : e.target.querySelector('[data-plps-unit]'));
      if (!tpl) return;
      closeModal();
      openUnit(tpl.getAttribute('data-plps-unit'), { trigger: 'editor' });
    });
    document.addEventListener('shopify:block:deselect', function (e) {
      var tpl = e.target && (e.target.matches('[data-plps-unit]') ? e.target : e.target.querySelector('[data-plps-unit]'));
      if (tpl) closeModal();
    });
    document.addEventListener('shopify:section:unload', function () { closeModal(); });
  } else if (previewUnit && root.querySelector('[data-plps-unit="' + previewUnit + '"]')) {
    state.variant = 'preview';
    openUnit(previewUnit, { trigger: 'preview' });
  } else {
    /* The identified probe waits on Klaviyo's onsite script, which the theme
       loads lazily via requestIdleCallback, so it can take up to three
       seconds to answer. Arming the gate behind it meant the scroll listener
       attached that late, and because it is a `once` listener a first scroll
       inside that window landed on nothing and was lost - the visitor then
       had to scroll a second time to see anything. The listeners now attach
       synchronously and the probe is resolved inside the dwell wait, which
       already has a six second budget to spend.

       Deferring the probe does not change who sees what. pickGateUnit()
       suppresses every unit except `pro` for a known subscriber, so the only
       thing the answer can still do by the time the dwell elapses is cancel
       a non-pro unit, which is exactly what the check below does. */
    identifiedPromise = klaviyoIdentified().then(function (identified) {
      if (identified) markSubscribed();
      return identified;
    }).catch(function () { return false; });
    armGate();
    armExitIntent();
  }

  // Safety net: never leave the page scroll-locked if something navigates away.
  window.addEventListener('pagehide', unlockScroll);
})();